import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Alert, CircularProgress, TextField,
  Tabs, Tab,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  HowToReg as ClaimIcon,
  OpenInNew as OpenIcon,
  CalendarMonth as CalendarIcon,
  Visibility as ShowingIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api, apiRoutes } from '../../api';
import TaskDetailPanel from './TaskDetailPanel';
import AgentCalendar from './AgentCalendar';
import './AgentAssignments.css';

const AgentAssignments = () => {
  const [activeTab, setActiveTab] = useState(0);

  // --- Property assignments ---
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // --- Inspection tasks ---
  const [claimableTasks, setClaimableTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState(null);
  const [taskActionLoading, setTaskActionLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // --- Showings ---
  const [showings, setShowings] = useState([]);
  const [showingsLoading, setShowingsLoading] = useState(false);
  const [showingsError, setShowingsError] = useState(null);
  const [showingActionLoading, setShowingActionLoading] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState({ open: false, showing: null });
  const [visitingWindows, setVisitingWindows] = useState([]);
  const [windowsLoading, setWindowsLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, showing: null });

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => {
    loadAssignments();
    loadInspectionTasks();
    loadShowings();
    // Initial dashboard hydration; loaders are intentionally stable for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------
  // Property assignments
  // ----------------------------------------------------------------
  const loadAssignments = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiRoutes.agents.propertyAssignments, { headers: authHeader() });
      setAssignments(res.data.results || res.data || []);
    } catch {
      setError('بارگذاری وظایف ناموفق بود');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignmentAction = async (assignmentId, action) => {
    setActionLoading(true);
    try {
      const actionMap = { accept: 'start_work', reject: 'reject_work', complete: 'complete_work' };
      const payload = action === 'reject' ? { rejection_reason: rejectionReason } : {};
      await api.post(
        apiRoutes.agents.propertyAssignmentAction(assignmentId, actionMap[action]),
        payload,
        { headers: authHeader() }
      );
      loadAssignments();
      setDialogOpen(false);
      setRejectionReason('');
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در انجام عملیات');
    } finally {
      setActionLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // Inspection tasks
  // ----------------------------------------------------------------
  const loadInspectionTasks = async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const [claimableRes, mineRes] = await Promise.all([
        api.get(apiRoutes.inspectionTasks.list, { params: { scope: 'claimable' }, headers: authHeader() }),
        api.get(apiRoutes.inspectionTasks.list, { params: { scope: 'mine' }, headers: authHeader() }),
      ]);
      setClaimableTasks(claimableRes.data.results || claimableRes.data || []);
      setMyTasks(mineRes.data.results || mineRes.data || []);
    } catch {
      setTasksError('بارگذاری وظایف بازرسی ناموفق بود');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleClaimTask = async (taskId) => {
    setTaskActionLoading(true);
    try {
      await api.post(apiRoutes.inspectionTasks.claim(taskId), {}, { headers: authHeader() });
      await loadInspectionTasks();
    } catch (err) {
      setTasksError(err.response?.data?.detail || 'خطا در دریافت وظیفه');
    } finally {
      setTaskActionLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // Showings
  // ----------------------------------------------------------------
  const loadShowings = async () => {
    setShowingsLoading(true);
    setShowingsError(null);
    try {
      const res = await api.get(apiRoutes.users.showings, { headers: authHeader() });
      setShowings(res.data.results || res.data || []);
    } catch {
      setShowingsError('بارگذاری درخواست‌های بازدید ناموفق بود');
    } finally {
      setShowingsLoading(false);
    }
  };

  const openScheduleDialog = async (showing) => {
    setScheduleDialog({ open: true, showing });
    setVisitingWindows([]);
    setWindowsLoading(true);
    try {
      const res = await api.get(
        apiRoutes.listings.schedules(showing.listing),
        { headers: authHeader() }
      );
      const windows = (res.data.results || res.data || []).map((w) => {
        const entry = { startTime: w.start_time, endTime: w.end_time };
        if (w.weekday != null) {
          entry.daysOfWeek = [(w.weekday + 1) % 7]; // backend 0=Mon → FullCalendar 0=Sun
        } else {
          entry.daysOfWeek = [new Date(w.date).getDay()];
          entry.startRecur = w.date;
          entry.endRecur = w.date;
        }
        return entry;
      });
      setVisitingWindows(windows);
    } catch {
      // visiting windows are optional
    } finally {
      setWindowsLoading(false);
    }
  };

  const handleVisitSelected = async (startStr, endStr) => {
    const { showing } = scheduleDialog;
    setShowingActionLoading(true);
    setShowingsError(null);
    try {
      await api.post(
        apiRoutes.users.scheduleShowing(showing.id),
        { slot: startStr, end: endStr },
        { headers: authHeader() }
      );
      setScheduleDialog({ open: false, showing: null });
      await loadShowings();
    } catch (e) {
      setShowingsError(
        e.response?.data?.detail || e.response?.data?.slot?.[0] || 'خطا در زمان‌بندی بازدید'
      );
    } finally {
      setShowingActionLoading(false);
    }
  };

  const handleCompleteShowing = async (showing) => {
    setShowingActionLoading(true);
    setShowingsError(null);
    try {
      await api.post(apiRoutes.users.completeShowing(showing.id), {}, { headers: authHeader() });
      await loadShowings();
    } catch (e) {
      setShowingsError(e.response?.data?.error || 'خطا در تکمیل بازدید');
    } finally {
      setShowingActionLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------
  const SHOWING_STATUS_COLOR = {
    REQUESTED: 'warning', CONFIRMED: 'primary', REJECTED: 'error',
    CANCELED: 'default', EXPIRED: 'default', COMPLETED: 'success',
  };
  const SHOWING_STATUS_LABEL = {
    REQUESTED: 'در انتظار', CONFIRMED: 'تأیید شده', REJECTED: 'رد شده',
    CANCELED: 'لغو شده', EXPIRED: 'منقضی', COMPLETED: 'انجام شده',
  };

  const fmtSlots = (slots) => {
    if (!Array.isArray(slots) || slots.length === 0) return '—';
    return slots
      .map((s) => {
        const start = typeof s === 'string' ? s : s.start;
        return new Date(start).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
      })
      .join(' | ');
  };

  const getStatusColor = (s) =>
    ({ pending: 'warning', in_progress: 'info', completed: 'success', cancelled: 'error' }[s] || 'default');

  const getStatusIcon = (s) =>
    ({ pending: <PendingIcon />, in_progress: <AssignmentIcon />, completed: <CheckCircleIcon />, cancelled: <CancelIcon /> }[s] || <AssignmentIcon />);

  const translateStatus = (s) =>
    ({
      pending: 'تخصیص‌یافته', in_progress: 'پذیرفته‌شده', completed: 'تکمیل‌شده', cancelled: 'ردشده',
      PENDING: 'در انتظار', CLAIMABLE: 'قابل دریافت', ASSIGNED: 'دریافت‌شده',
      SCHEDULED: 'زمان‌بندی‌شده', IN_PROGRESS: 'در جریان', COMPLETED: 'تکمیل‌شده', CANCELLED: 'لغوشده',
    }[s] || s);

  const translateTaskStatus = (s) =>
    ({
      CLAIMABLE: 'warning', ASSIGNED: 'info', SCHEDULED: 'primary',
      IN_PROGRESS: 'secondary', COMPLETED: 'success', CANCELLED: 'error', PENDING: 'default',
    }[s] || 'default');

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const getAvailableActions = (a) => {
    if (['pending', 'assigned'].includes(a.status))
      return [{ label: 'پذیرش', action: 'accept', color: 'success' }, { label: 'رد', action: 'reject', color: 'error' }];
    if (a.status === 'in_progress')
      return [{ label: 'تکمیل', action: 'complete', color: 'primary' }];
    return [];
  };

  if (loading && tasksLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box dir="rtl" className="agents-section agents-assignments">
      {/* Header */}
      <Box className="agents-section-head" display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>پنل نماینده</Typography>
          <Typography variant="body2" color="text.secondary">مدیریت وظایف و بازرسی‌های ملکی</Typography>
        </Box>
        <Tooltip title="تازه‌سازی">
          <IconButton
            onClick={() => { loadAssignments(); loadInspectionTasks(); loadShowings(); }}
            disabled={loading || tasksLoading || showingsLoading}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {(error || tasksError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); setTasksError(null); }}>
          {error || tasksError}
        </Alert>
      )}
      {showingsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowingsError(null)}>
          {showingsError}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label={`وظایف ملکی (${assignments.length})`} />
        <Tab
          label={`بازرسی‌های قابل دریافت (${claimableTasks.length})`}
          icon={<SearchIcon fontSize="small" />} iconPosition="end"
        />
        <Tab
          label={`بازرسی‌های من (${myTasks.length})`}
          icon={<ClaimIcon fontSize="small" />} iconPosition="end"
        />
        <Tab
          label={`بازدیدها (${showings.filter(s => s.status === 'REQUESTED').length})`}
          icon={<ShowingIcon fontSize="small" />} iconPosition="end"
        />
      </Tabs>

      {/* ── Tab 0: Property Assignments ── */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {['pending', 'in_progress', 'completed', 'cancelled'].map((s) => (
                <Grid item xs={6} sm={3} key={s}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box display="flex" justifyContent="center" mb={1}>{getStatusIcon(s)}</Box>
                      <Typography variant="h4" color={`${getStatusColor(s)}.main`}>
                        {assignments.filter(a => a.status === s).length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{translateStatus(s)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>مالک</TableCell>
                        <TableCell>املاک</TableCell>
                        <TableCell>وضعیت</TableCell>
                        <TableCell>تاریخ تخصیص</TableCell>
                        <TableCell>اقدامات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            <Typography variant="body2">{a.owner?.first_name} {a.owner?.last_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{a.owner?.username}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{a.properties?.length || 0} ملک</Typography>
                            {a.properties?.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {a.properties[0].address}{a.properties.length > 1 && ` +${a.properties.length - 1}`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip icon={getStatusIcon(a.status)} label={translateStatus(a.status)} color={getStatusColor(a.status)} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(a.assigned_at)}</Typography>
                            {a.completed_at && (
                              <Typography variant="caption" color="text.secondary">تکمیل: {formatDate(a.completed_at)}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              {getAvailableActions(a).map((item) => (
                                <Button
                                  key={item.action} size="small" variant="outlined" color={item.color}
                                  disabled={actionLoading}
                                  onClick={() => {
                                    if (item.action === 'reject') { setSelectedAssignment(a); setDialogOpen(true); }
                                    else handleAssignmentAction(a.id, item.action);
                                  }}
                                >
                                  {item.label}
                                </Button>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {assignments.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <AssignmentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">هیچ وظیفه‌ای یافت نشد</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Tab 1: Claimable Inspection Tasks ── */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>بازرسی‌های قابل دریافت در محله‌های شما</Typography>
            {tasksLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ملک</TableCell>
                      <TableCell>آدرس</TableCell>
                      <TableCell>محله</TableCell>
                      <TableCell>وضعیت</TableCell>
                      <TableCell>تاریخ ایجاد</TableCell>
                      <TableCell>اقدام</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claimableTasks.map((task) => (
                      <TableRow key={task.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {task.property?.name || `ملک ${String(task.property?.id || '').slice(0, 8)}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {task.property?.address || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{task.property?.neighborhood?.name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={translateStatus(task.status)} color={translateTaskStatus(task.status)} size="small" />
                        </TableCell>
                        <TableCell>{formatDate(task.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            size="small" variant="contained" color="primary"
                            startIcon={<ClaimIcon />}
                            onClick={() => handleClaimTask(task.id)}
                            disabled={taskActionLoading}
                          >
                            دریافت وظیفه
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!tasksLoading && claimableTasks.length === 0 && (
              <Box textAlign="center" py={4}>
                <SearchIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">هیچ بازرسی قابل دریافتی در محله‌های شما وجود ندارد</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Tab 2: My Inspection Tasks ── */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            {selectedTask ? (
              <TaskDetailPanel
                task={selectedTask}
                onBack={() => setSelectedTask(null)}
                onTaskUpdated={loadInspectionTasks}
              />
            ) : (
              <>
                <Typography variant="h6" gutterBottom>بازرسی‌های دریافت‌شده توسط من</Typography>
                {tasksLoading ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ملک</TableCell>
                          <TableCell>آدرس</TableCell>
                          <TableCell>وضعیت</TableCell>
                          <TableCell>تاریخ به‌روزرسانی</TableCell>
                          <TableCell>اقدام</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {myTasks.map((task) => (
                          <TableRow key={task.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedTask(task)}>
                            <TableCell>
                              <Typography variant="body2">
                                {task.property?.name || `ملک ${String(task.property?.id || '').slice(0, 8)}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {task.property?.address || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={translateStatus(task.status)} color={translateTaskStatus(task.status)} size="small" />
                            </TableCell>
                            <TableCell>{formatDate(task.updated_at)}</TableCell>
                            <TableCell>
                              <Tooltip title="مشاهده جزئیات و اقدام">
                                <Button size="small" variant="outlined" startIcon={<OpenIcon />}
                                  onClick={e => { e.stopPropagation(); setSelectedTask(task); }}>
                                  باز کردن
                                </Button>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                {!tasksLoading && myTasks.length === 0 && (
                  <Box textAlign="center" py={4}>
                    <ClaimIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">هنوز هیچ بازرسی دریافت نکرده‌اید</Typography>
                    <Typography variant="body2" color="text.secondary">از تب «بازرسی‌های قابل دریافت» وظیفه بگیرید</Typography>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Tab 3: Showings ── */}
      {activeTab === 3 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            {showingsLoading ? (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>شناسه آگهی</TableCell>
                      <TableCell>آدرس ملک</TableCell>
                      <TableCell>محله</TableCell>
                      <TableCell>زمان‌های پیشنهادی</TableCell>
                      <TableCell>وضعیت</TableCell>
                      <TableCell>تاریخ درخواست</TableCell>
                      <TableCell>اقدام</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {showings.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Typography variant="body2">{s.listing_info?.id || s.listing}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {s.listing_info?.address || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.listing_info?.neighborhood || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {fmtSlots(s.offered_slots)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={SHOWING_STATUS_LABEL[s.status] || s.status}
                            color={SHOWING_STATUS_COLOR[s.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(s.created_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title="جزئیات">
                              <IconButton size="small" onClick={() => setDetailDialog({ open: true, showing: s })}>
                                <OpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {s.status === 'REQUESTED' && (
                              <Button
                                size="small" variant="contained" startIcon={<CalendarIcon />}
                                onClick={() => openScheduleDialog(s)}
                                disabled={showingActionLoading}
                              >
                                زمان‌بندی
                              </Button>
                            )}
                            {s.status === 'CONFIRMED' && (
                              <Button
                                size="small" variant="outlined" color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleCompleteShowing(s)}
                                disabled={showingActionLoading}
                              >
                                تکمیل
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!showingsLoading && showings.length === 0 && (
              <Box textAlign="center" py={6}>
                <ShowingIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">هیچ درخواست بازدیدی وجود ندارد</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Rejection Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>رد وظیفه</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>لطفاً دلیل رد این وظیفه را ارائه دهید:</Typography>
          <TextField
            fullWidth multiline rows={3} label="دلیل رد"
            value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="توضیح دهید چرا این وظیفه را رد می‌کنید..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>لغو</Button>
          <Button
            onClick={() => handleAssignmentAction(selectedAssignment?.id, 'reject')}
            color="error" variant="contained"
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? 'در حال رد...' : 'رد وظیفه'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Schedule Dialog ── */}
      <Dialog
        open={scheduleDialog.open}
        onClose={() => setScheduleDialog({ open: false, showing: null })}
        maxWidth="lg" fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">انتخاب زمان بازدید</Typography>
            <IconButton onClick={() => setScheduleDialog({ open: false, showing: null })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {scheduleDialog.showing && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">زمان‌های پیشنهادی کاربر:</Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                {(scheduleDialog.showing.offered_slots || []).map((slot, i) => {
                    const start = typeof slot === 'string' ? slot : slot.start;
                    const end   = typeof slot === 'string' ? null : slot.end;
                    const label = new Date(start).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' })
                      + (end ? ` — ${new Date(end).toLocaleString('fa-IR', { timeStyle: 'short' })}` : '');
                    return (
                      <Chip key={i} label={label} size="small" color="info" variant="outlined" />
                    );
                  })}
              </Box>
            </Box>
          )}
          {windowsLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <AgentCalendar
              visitMode
              taskLabel={scheduleDialog.showing?.listing_title || ''}
              onVisitSelected={(startStr, endStr) => handleVisitSelected(startStr, endStr)}
              visitingWindows={visitingWindows}
              offeredSlots={scheduleDialog.showing?.offered_slots || []}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog({ open: false, showing: null })}>لغو</Button>
        </DialogActions>
      </Dialog>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, showing: null })}
        maxWidth="sm" fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">جزئیات درخواست بازدید</Typography>
            <IconButton onClick={() => setDetailDialog({ open: false, showing: null })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailDialog.showing && (
            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">وضعیت</Typography>
                <Chip
                  label={SHOWING_STATUS_LABEL[detailDialog.showing.status]}
                  color={SHOWING_STATUS_COLOR[detailDialog.showing.status]}
                  size="small"
                />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">تاریخ درخواست</Typography>
                <Typography variant="body2">{formatDate(detailDialog.showing.created_at)}</Typography>
              </Box>
              {detailDialog.showing.event?.start_at && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">زمان تأیید شده</Typography>
                  <Typography variant="body2">
                    {new Date(detailDialog.showing.event.start_at).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' })}
                  </Typography>
                </Box>
              )}
              {detailDialog.showing.notes && (
                <Box>
                  <Typography variant="body2" color="text.secondary">یادداشت</Typography>
                  <Typography variant="body2">{detailDialog.showing.notes}</Typography>
                </Box>
              )}
              {detailDialog.showing.rating && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">امتیاز کاربر</Typography>
                  <Typography variant="body2">{'★'.repeat(detailDialog.showing.rating)}</Typography>
                </Box>
              )}
              {detailDialog.showing.comment && (
                <Box>
                  <Typography variant="body2" color="text.secondary">نظر کاربر</Typography>
                  <Typography variant="body2">{detailDialog.showing.comment}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, showing: null })}>بستن</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AgentAssignments;
